/**
 * Les charges d'exploitation d'un bien, rangées par nature (vue financière).
 *
 * Le moteur financier ne remontait que leur total (`totalOperatingExpenses`) :
 * impossible de dire à l'écran « 3 400 € de taxe foncière, 1 200 € d'assurance ».
 * Les six natures existent pourtant déjà dans `UnifiedPropertyFinancials`, avec
 * leur provenance (`actual` / `estimated` / `projected`). On les garde.
 *
 * Logique pure, sans DI : elle se teste sans échafaudage.
 */

import { TemporalFinancialAmount, UnifiedPropertyFinancials } from '../../../shared/definitions/unified-financial-data.model';

export const EXPENSE_KEYS = ['propertyTax', 'insurance', 'maintenance', 'management', 'utilities', 'other'] as const;
export type ExpenseKey = (typeof EXPENSE_KEYS)[number];

export interface ExpenseBreakdown {
  /** Montants ANNUELS, par nature. */
  annual: Record<ExpenseKey, number>;
  /**
   * Natures dont le montant est une estimation du moteur, pas une saisie du
   * propriétaire : c'est ce qu'il faut lui demander de renseigner. Une saisie
   * porte la provenance `projected` côté moteur (cf. `data-normalization`).
   */
  estimated: ExpenseKey[];
}

const num = (v: unknown): number => (typeof v === 'number' && isFinite(v) ? v : 0);

/** Ramène un montant temporel à l'année, quelle que soit sa périodicité. */
export function annualAmount(a: TemporalFinancialAmount | undefined | null): number {
  if (!a) return 0;
  const amount = num(a.amount);
  switch (a.period) {
    case 'monthly':
      return amount * 12;
    case 'quarterly':
      return amount * 4;
    default:
      return amount;
  }
}

/** Les six natures de charges d'un bien, ou undefined quand le moteur n'a rien rendu. */
export function expenseBreakdownOf(
  unified: Pick<UnifiedPropertyFinancials, 'propertyTax' | 'insurance' | 'maintenance' | 'managementFee' | 'utilities' | 'otherExpenses'> | null | undefined
): ExpenseBreakdown | undefined {
  if (!unified) return undefined;
  const src: Record<ExpenseKey, TemporalFinancialAmount | undefined> = {
    propertyTax: unified.propertyTax,
    insurance: unified.insurance,
    maintenance: unified.maintenance,
    management: unified.managementFee,
    utilities: unified.utilities,
    other: unified.otherExpenses
  };
  const annual = {} as Record<ExpenseKey, number>;
  const estimated: ExpenseKey[] = [];
  for (const k of EXPENSE_KEYS) {
    annual[k] = annualAmount(src[k]);
    // Un montant à zéro n'est pas « estimé » : il n'y a rien à confirmer.
    if (annual[k] > 0 && src[k]?.source === 'estimated') estimated.push(k);
  }
  return { annual, estimated };
}
